'use client';

import { InventaireUpdateInput } from '@repo/types';
import { Alert, AlertDescription } from '@repo/ui/components/ui/alert';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Input } from '@repo/ui/components/ui/input';
import { Label } from '@repo/ui/components/ui/label';
import { Progress } from '@repo/ui/components/ui/progress';
import { AlertTriangle, ArrowLeft, ArrowRight, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useInventoryData } from '../../../../hooks/use-inventory-data';
import jsonFields from '../../../../lib/inventory/data/fields-tree.json';
import { useInventaire, useUpdateInventaire } from '../../hooks';
import { useUpdateArbre } from '../../hooks/use-arbre';
import { FormField } from '../../shared/components/form-field';
import { StepNavigation } from '../../shared/components/step-navigation';
import { FormData } from '../../shared/types';
import { BevaStep } from './components/beva-step';
import { FungusSelect } from './components/fungus-select';
import { IdentificationStep } from './components/identification-step';
import { ImageStep } from './components/image-step';
import { ParasiteSelect } from './components/parasite-select';
import { RisqueRuptureTable } from './components/risque-rupture-table';
import { TravauxStep } from './components/travaux-step';
import { getEditSteps } from './steps-config';
import { mapFormDataToServerData, mapServerDataToFormData } from './utils/data-mapper';
import { getDynamicFieldOptions } from './utils/dynamic-field-options';
import { useRouter } from '../../../../hooks/use-router';

interface InventaireServerResponse {
  data: {
    id: number;
    created_at: string;
    updated_at: string;
    is_finished: boolean;
    type: 'ARBRE';
    variety_grade: number;
    health_index: number;
    aesthetic_index: number;
    location_index: number;
    address: string;
    ville: string;
    aesthetic_column: number;
    health_column: number;
    user_id: number;
    arbre_id: number;
    epaysage_id?: number | null;
    epaysage?: any;
    arbre: {
      id: number;
      diametre: number;
      created_at: string;
      updated_at: string;
      coord: {
        lat: number;
        lng: number;
      };
      code_site: string;
      num_sujet: string;
      critere: string[];
      implantation: string;
      domaine: string;
      nuisance: string[];
      proximite: string[];
      taux_freq: string;
      type_passage: string[];
      accessibilite: string;
      abattage: string;
      travaux_collet?: string | null;
      travaux_tronc?: string | null;
      travaux_houppier?: string | null;
      date_travaux: string;
      date_pro_visite: string;
      com_pro_visite?: string | null;
      caract_pied: string[];
      caract_tronc: string;
      hauteur: number;
      port_arbre: string;
      stade_dev: string;
      etat_san_collet: string[];
      etat_san_tronc: string[];
      etat_san_houppier: string[];
      address: string;
      com_access?: string | null;
      dict?: any;
      risque: any[];
      proximite_other?: string | null;
      accessibilite_other?: string | null;
      caract_pied_other?: string | null;
      caract_tronc_multiples?: number | null;
      etat_san_collet_cavite?: string | null;
      etat_san_collet_champignons: string[];
      etat_san_tronc_cavite?: string | null;
      etat_san_tronc_corps_etranger?: string | null;
      etat_san_tronc_champignons: string[];
      etat_san_tronc_nuisibles: string[];
      etat_san_houppier_champignons: string[];
      etat_san_houppier_nuisibles: string[];
      risque_general: string[];
      travaux_commentaire?: string | null;
      critere_other?: string | null;
      risque_general_other?: string | null;
      type_passage_other?: string | null;
      type_intervention: string;
      etat_san_general: string[];
      user_edited_date_travaux: string;
      travaux_tronc_other?: string | null;
      travaux_collet_other?: string | null;
      travaux_houppier_other?: string | null;
      proximite_with_dict: any[];
      ville: string;
      pays: string;
      travaux_tronc_protection?: string | null;
      etat_san_tronc_nuisibles_autres?: string | null;
      etat_san_houppier_nuisibles_autres?: string | null;
      travaux_collet_multiple: string[];
      travaux_tronc_multiple: string[];
      travaux_houppier_multiple: string[];
      status_travaux: boolean;
      etat_san_collet_champignons_autres?: string | null;
      etat_san_tronc_champignons_autres?: string | null;
      etat_san_houppier_champignons_autres?: string | null;
      etat_san_collet_other?: string | null;
      etat_san_tronc_other?: string | null;
      etat_san_houppier_other?: string | null;
      etat_san_general_other?: string | null;
      espece_id: number;
      espece: {
        id: number;
        name: string;
        genre: string;
        nomFr: string;
        cultivar?: string | null;
      };
      imgUrl: {
        img1?: string | null;
        img2?: string | null;
        img3?: string | null;
      };
    };
    user: {
      username: string;
      email: string;
      id: number;
    };
  };
}

interface EditArbreProps {
  inventaireId?: number;
  initialData?: InventaireServerResponse;
}

export default function EditArbre({ inventaireId, initialData }: EditArbreProps = {}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({});
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [originalData, setOriginalData] = useState<InventaireServerResponse | null>(null);
  const [viewMode, setViewMode] = useState<'horizontal' | 'vertical'>('horizontal');
  const router = useRouter();
  // Load inventory data (champignons, especes, nuisibles)
  const {
    champignons,
    especes,
    nuisibles,
    isLoading: isLoadingInventoryData,
    hasError: hasInventoryDataError,
    champignonsError,
    especesError,
    nuisiblesError,
  } = useInventoryData(true); // Auto-load data

  const { data, isLoading, error } = useInventaire(inventaireId);
  const { mutateAsync: updateArbre } = useUpdateArbre();
  const { mutateAsync: updateInventaire } = useUpdateInventaire();

  // Get inventory type for dynamic steps configuration
  const inventaireType = data?.data?.type || initialData?.data?.type || 'ARBRE';
  const steps = useMemo(() => getEditSteps(inventaireType), [inventaireType]);

  // Calculate completion percentage
  const calculateCompletion = () => {
    const totalFields = steps.reduce((acc, step) => {
      return acc + step.fields.filter((field) => {
        // Count only non-hidden fields
        if (field.hiddenWhen && data?.data?.type) {
          return !field.hiddenWhen(data.data.type);
        }
        return true;
      }).length;
    }, 0);
    const completedFields = Object.keys(formData).filter(key => {
      const value = formData[key];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'number') return value !== null && value !== undefined;
      return value !== null && value !== undefined;
    }).length;
    return totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
  };

  // Check if current step is completed
  const isStepCompleted = (stepIndex: number) => {
    const step = steps[stepIndex];
    if (!step) return false;
    return step.fields
      .filter((field) => {
        // Skip hidden fields in completion check
        if (field.hiddenWhen && data?.data?.type) {
          return !field.hiddenWhen(data.data.type);
        }
        return true;
      })
      .some(field => {
        const value = formData[field.name];
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'string') return value.trim() !== '';
        if (typeof value === 'number') return value !== null && value !== undefined;
        return value !== null && value !== undefined;
      });
  };

  // Validate current step
  const validateCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return false;
    const newErrors: { [key: string]: string } = {};

    step.fields
      .filter((field) => {
        // Skip hidden fields in validation
        if (field.hiddenWhen && data?.data?.type) {
          return !field.hiddenWhen(data.data.type);
        }
        return true;
      })
      .forEach(field => {
        const value = formData[field.name];

        // if (field.required) {
        //   if (Array.isArray(value) && value.length === 0) {
        //     newErrors[field.name] = 'Ce champ est requis';
        //   } else if (typeof value === 'string' && value.trim() === '') {
        //     newErrors[field.name] = 'Ce champ est requis';
        //   } else if (value === null || value === undefined) {
        //     newErrors[field.name] = 'Ce champ est requis';
        //   }
        // }

        const jsonField: any = (jsonFields as any)[field.name];
        const rules: Record<string, string> | undefined = jsonField?.rg;
        const freeforms: any[] = jsonField?.freeforms || [];
        const ffById: Record<string, any> = {};
        freeforms.forEach((ff) => { ffById[ff.id] = ff; });

        // if (rules) {
        //   Object.entries(rules).forEach(([optionId, rule]) => {
        //     if (rule?.startsWith('onlyWith_')) {
        //       const ffId = rule.replace('onlyWith_', '');
        //       const ff = ffById[ffId] || freeforms.find((f) => (f.server || '').endsWith(ffId));
        //       const ffKey = ff?.server;
        //       const isSelected = Array.isArray(value) ? value.includes(optionId) : value === optionId;
        //       if (isSelected && ffKey) {
        //         const ffVal = formData[ffKey];
        //         const empty = ffVal === undefined || ffVal === null || (typeof ffVal === 'string' && ffVal.trim() === '');
        //         if (empty) {
        //           newErrors[field.name] = `Veuillez renseigner: ${ff?.label || ffKey}`;
        //         }
        //       }
        //     }
        //   });
        // }
      });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Load initial data
  useEffect(() => {
    if (data) {
      const mappedData = mapServerDataToFormData(data as any);
      setFormData(mappedData);
      setOriginalData(data as any);
    }
  }, [inventaireId, data]);

  // Update completion status
  useEffect(() => {
    const newCompletedSteps = new Set<number>();
    steps.forEach((_, index) => {
      if (isStepCompleted(index)) {
        newCompletedSteps.add(index);
      }
    });
    setCompletedSteps(newCompletedSteps);
  }, [formData, steps]);

  // Load inventaire data from server

  const handleFieldChange = (fieldName: string, value: any) => {
    // Apply oneOf rule if applicable
    const jsonField: any = (jsonFields as any)[fieldName];
    const oneOfValues = jsonField?.oneOf || [];

    let processedValue = value;

    // If field has oneOf rule and value is an array (multiselect)
    if (oneOfValues.length > 0 && Array.isArray(value)) {
      // Check if any oneOf option is being selected
      const selectedOneOfOptions = value.filter((v: any) => oneOfValues.includes(v));

      // If a oneOf option is selected, ensure only one oneOf option remains
      if (selectedOneOfOptions.length > 1) {
        // Keep only the last selected oneOf option
        const lastSelectedOneOf = selectedOneOfOptions[selectedOneOfOptions.length - 1];
        processedValue = [
          ...value.filter((v: any) => !oneOfValues.includes(v) || v === lastSelectedOneOf)
        ];
      } else if (selectedOneOfOptions.length === 1) {
        // Ensure no other oneOf options are selected
        processedValue = [
          ...value.filter((v: any) => !oneOfValues.includes(v) || v === selectedOneOfOptions[0])
        ];
      }
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };



  const renderFreeforms = (fieldName: string) => {
    const jsonField: any = (jsonFields as any)[fieldName];
    if (!jsonField?.freeforms) return null;

    // Get current field values to check if freeform options are selected
    const currentValues = Array.isArray(formData[fieldName]) ? formData[fieldName] : [formData[fieldName]].filter(Boolean);

    return (
      <div className="space-y-3">
        {jsonField.freeforms.map((ff: any) => {
          const ffKey = `${fieldName}_${ff.id}`;
          const ffValue = formData[ffKey] || '';
          const ffType = (ff.type || 'String').toLowerCase();
          const label = ff.label || 'Autre, précisez :';

          // Show freeform input if the corresponding option is selected OR if it already has a value
          const isSelected = currentValues.includes(ff.id);
          const hasValue = ffValue && (typeof ffValue === 'string' ? ffValue.trim() !== '' : Array.isArray(ffValue) ? ffValue.length > 0 : true);
          if (!isSelected && !hasValue) return null;

          if (ffType === 'integer') {
            return (
              <div key={ff.id} className="space-y-2">
                <Label htmlFor={`${fieldName}-${ff.id}`} className="text-sm font-medium">{label}</Label>
                <Input
                  id={`${fieldName}-${ff.id}`}
                  type="number"
                  value={ffValue}
                  onChange={(e) => handleFieldChange(ffKey, Number(e.target.value))}
                  placeholder="Entrer un nombre"
                />
              </div>
            );
          }

          if (ffType === 'array' && ff.constraint === 'Fungus') {
            return (
              <FungusSelect
                key={ff.id}
                champignons={champignons}
                value={Array.isArray(ffValue) ? ffValue : []}
                onChange={(val: number[]) => handleFieldChange(ffKey, val)}
                label={label}
                fieldId={`${fieldName}-${ff.id}`}
                placeholder="Sélectionnez des champignons..."
              />
            );
          }

          if (ffType === 'array' && ff.constraint === 'Parasites') {
            return (
              <ParasiteSelect
                key={ff.id}
                nuisibles={nuisibles}
                value={Array.isArray(ffValue) ? ffValue : []}
                onChange={(val: number[]) => handleFieldChange(ffKey, val)}
                label={label}
                fieldId={`${fieldName}-${ff.id}`}
                placeholder="Sélectionnez des nuisibles..."
              />
            );
          }

          return (
            <div key={ff.id} className="space-y-2">
              <Label htmlFor={`${fieldName}-${ff.id}`} className="text-sm font-medium">{label}</Label>
              <Input
                id={`${fieldName}-${ff.id}`}
                type="text"
                value={ffValue}
                onChange={(e) => handleFieldChange(ffKey, e.target.value)}
                placeholder={ffType === 'array' ? 'Entrez des valeurs séparées par des virgules' : 'Saisir ici'}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const handleNext = async () => {
    if (!validateCurrentStep() || currentStep >= steps.length - 1) {
      return;
    }

    try {
      await saveData();
      // Move to next step after successful save
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error saving before next step:', error);
      // Don't move to next step if save fails
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBack = () => {
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  };

  const extractInventaireDataServer = (data: any): InventaireUpdateInput => {
    return {
      id: data.data.id,
      is_finished: data.data.is_finished,
      variety_grade: data.data.variety_grade,
      health_index: data.data.health_index,
      aesthetic_index: data.data.aesthetic_index,
      location_index: data.data.location_index,
      address: data.data.address,
      ville: data.data.ville,
      aesthetic_column: data.data.aesthetic_column,
      health_column: data.data.health_column,
      epaysage_id: data.data.epaysage_id,
      arbre_id: data.data.arbre_id,
    };
  };

  // Common save function to avoid code duplication
  const saveData = async (): Promise<void> => {
    console.log('Saving form data:', formData);
    if (originalData && data && originalData.data.arbre_id && data.data.id) {
      const dataMaped = mapFormDataToServerData(formData, data?.data as any).data?.arbre;
      const dataMapedInventaire = extractInventaireDataServer(mapFormDataToServerData(formData, data?.data as any));

      await Promise.all([
        updateArbre({
          id: originalData.data.arbre_id,
          data: dataMaped as any
        }),
        updateInventaire({
          id: data.data.id,
          data: dataMapedInventaire as any
        })
      ]);
    }
    console.log('Data saved successfully');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveData();
      if (inventaireId) {
        router.push(`/admin/inventaires/${inventaireId}`);
      }
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setTimeout(() => setIsSaving(false), 1000);
    }
  };

  const currentStepData = steps[currentStep];
  const completionPercentage = calculateCompletion();
  const hasErrors = Object.keys(errors).length > 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des données...</p>
        </div>
      </div>
    );
  }

  // Early return if no current step data
  if (!currentStepData) {
    return (
      <div className="">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-4">Erreur</h1>
          <p className="text-muted-foreground">Aucune étape trouvée.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col relative">
      {/* Loading Overlay */}
      {isSaving && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-card rounded-lg p-8 shadow-xl max-w-sm w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold mb-2">Sauvegarde en cours...</h3>
              <p className="text-sm text-muted-foreground">
                Veuillez patienter pendant que nous sauvegardons vos modifications.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-4">
        <Button
          variant="outline"
          onClick={handleBack}
          className="flex items-center space-x-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour</span>
        </Button>
        <div className="flex-1 text-right md:text-left">
          <h1 className="text-2xl font-semibold mb-1">
            {originalData ? 'Édition d\'inventaire arbre' : 'Création d\'inventaire arbre'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {originalData
              ? `Arbre ${originalData.data.arbre.espece?.nomFr || 'Inconnu'} - ${originalData.data.arbre.address || ''}`
              : 'Complétez les informations de l\'arbre étape par étape'
            }
          </p>
          {originalData && (
            <p className="text-xs text-muted-foreground mt-1">
              ID: {originalData.data.id} | Dernière modification: {new Date(originalData.data.updated_at).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {/* <Card className="mb-3">
        <CardContent className="pt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Progression globale</span>
            <span className="text-sm text-muted-foreground">{completionPercentage}%</span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
        </CardContent>
      </Card> */}

      {/* Main Content Area */}
      <div className={`flex flex-1 gap-4 ${viewMode === 'vertical' ? 'flex-row' : 'flex-col'} ${isSaving ? 'pointer-events-none opacity-50' : ''}`}>
        {/* Steps Navigation */}
        {viewMode === 'vertical' && (
          <StepNavigation
            steps={steps}
            currentStep={currentStep}
            completedSteps={completedSteps}
            onStepClick={setCurrentStep}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        )}

        {/* Form Content */}
        <div className={`flex flex-col flex-1 ${viewMode === 'vertical' ? 'min-w-0' : ''}`}>
          {/* Horizontal Steps Navigation */}
          {viewMode === 'horizontal' && (
            <StepNavigation
              steps={steps}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          )}

          {/* Current Step Info */}
          <Card className="mb-3">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">
                {currentStepData.title}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{currentStepData.description}</p>
            </CardHeader>
          </Card>

          {/* Error Alert */}
          {hasErrors && (
            <Alert className="mb-3 border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                Veuillez corriger les erreurs avant de continuer.
              </AlertDescription>
            </Alert>
          )}

          {/* Inventory Data Loading Alert */}
          {isLoadingInventoryData && (
            <Alert className="mb-3 border-blue-200 bg-blue-50">
              <AlertTriangle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Chargement des données d'inventaire (champignons, espèces, nuisibles)...
              </AlertDescription>
            </Alert>
          )}

          {/* Inventory Data Error Alert */}
          {hasInventoryDataError && (
            <Alert className="mb-3 border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                Erreur lors du chargement des données d'inventaire:
                {champignonsError && ` Champignons: ${champignonsError}`}
                {especesError && ` Espèces: ${especesError}`}
                {nuisiblesError && ` Nuisibles: ${nuisiblesError}`}
              </AlertDescription>
            </Alert>
          )}

          {/* Form Content */}
          <Card className="flex-1 overflow-y-auto bg-card">
            <CardContent className="pt-4">
              {currentStepData.id === 'identification' ? (
                <IdentificationStep
                  formData={formData}
                  errors={errors}
                  champignons={champignons}
                  especes={especes}
                  nuisibles={nuisibles}
                  onChange={handleFieldChange}
                  renderFreeforms={renderFreeforms}
                />
              ) : currentStepData.id === 'images' ? (
                <ImageStep
                  formData={formData}
                  errors={errors}
                  onChange={handleFieldChange}
                  arbreId={originalData?.data.arbre_id}
                />
              ) : currentStepData.id === 'preconisation-travaux' ? (
                <TravauxStep
                  formData={formData}
                  errors={errors}
                  onChange={handleFieldChange}
                  renderFreeforms={renderFreeforms}
                />
              ) : currentStepData.id === 'estimation-valeur' ? (
                <BevaStep
                  formData={formData}
                  errors={errors}
                  onChange={handleFieldChange}
                  inventaireId={inventaireId}
                />
              ) : (
                <div className="space-y-4">
                  {currentStepData.fields
                    .filter((field) => {
                      // Hide field if hiddenWhen condition is met
                      if (field.hiddenWhen && data?.data?.type) {
                        return !field.hiddenWhen(data.data.type);
                      }
                      return true;
                    })
                    .map((field) => {
                      const dynamicOptions = getDynamicFieldOptions(field, champignons, especes, nuisibles);

                      // Special handling for risque-de-rupture field
                      if (field.name === 'risque-de-rupture') {
                        return (
                          <RisqueRuptureTable
                            key={field.name}
                            value={formData[field.name] as any}
                            onChange={(value) => handleFieldChange(field.name, value)}
                            label={field.label}
                            fieldId={field.name}
                            error={errors[field.name]}
                          />
                        );
                      }

                      return (
                        <FormField
                          key={field.name}
                          field={field}
                          value={formData[field.name] || ''}
                          error={errors[field.name]}
                          onChange={handleFieldChange}
                          renderFreeforms={renderFreeforms}
                          dynamicOptions={dynamicOptions}
                        />
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-3">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Précédent</span>
            </Button>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
              </Button>

              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={handleNext}
                  disabled={hasErrors}
                  className="flex items-center space-x-2"
                >
                  <span>Suivant</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSave}
                  disabled={hasErrors || isSaving}
                  className="flex items-center space-x-2"
                >
                  <span>{isSaving ? 'Sauvegarde...' : 'Terminer'}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
